import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Box,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { StyledTableRow } from '../styled/TableComponents';
import { formatTimestamp } from '../../../utils/dateUtils';
import { getProxiedUrl } from '../../../utils/userUtils';

const themeColors = {
  primary: '#232536',
  hover: '#3a3e5a',
};

const getUsername = (user) => {
  const candidate = user?.username ?? user?.userName;
  return typeof candidate === 'string' ? candidate.trim().replace(/^@+/, '') : '';
};

const LiveUsersTable = ({ users, loading, onUserClick }) => {
  if (loading) {
    return (
      <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Loading live users...
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2, mb: 4 }}>
      <Table>
        <TableHead sx={{ bgcolor: '#f8fafc' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Session Expires</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align='center'>
                <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
                  No live users found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => {
              const username = getUsername(user);

              return (
                <StyledTableRow
                  key={user.email}
                  onClick={() => onUserClick?.(user)}
                  sx={{
                    cursor: onUserClick ? 'pointer' : 'default',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={getProxiedUrl(user.profilePictureUrl)}
                        alt={user.name}
                        imgProps={{
                          onError: (e) => {
                            e.target.src = '';
                          },
                          referrerPolicy: 'no-referrer',
                        }}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: '#3b82f6',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                        }}
                      >
                        {user.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {user.name || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {username ? (
                      <MuiLink
                        component={RouterLink}
                        to={`/@${encodeURIComponent(username)}`}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          fontWeight: 500,
                          color: themeColors.primary,
                          textDecoration: 'none',
                          '&:hover': {
                            color: themeColors.hover,
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        @{username}
                      </MuiLink>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{user.email || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {formatTimestamp(user.lastSessionExpiryAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {formatTimestamp(user.lastLogin)}
                    </Typography>
                  </TableCell>
                </StyledTableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LiveUsersTable;
